// PLAN-cockpit-v2-iter1 Phase 5: 全 Lens 共通 Detail panel
// read mode ↔ edit mode toggle。Article のみ PATCH form (Entry は P1 immutable)
// Edit: visibility / lifecycle / stale_by / review_by / source_project + Advanced (previous_version)
// + Domain curated overlay editor (DomainOverlaySection を edit mode 内で表示)

import { useCallback, useEffect, useState } from 'react'
import { Detail } from './common/Detail'
import { EmptyState } from './common/EmptyState'
import { useDetailFetch } from '../hooks/useDetailFetch'
import { useToast } from './common/Toast'
import { apiFetch } from '../../api/client'
import { DomainOverlaySection } from './DomainOverlaySection'
import { pushRecentlyOpened } from '../hooks/useRecentlyOpened'
import { PermalinkButton } from './PermalinkButton'
import { JudgmentFeedbackPanel } from './JudgmentFeedbackPanel'

interface Props {
  id: string
  itemType: 'entry' | 'article'
  onClose: () => void
  onUnauthorized?: () => void
  highlightTerms?: string[]
}

export function SharedDetailPanel({ id, itemType, onClose, onUnauthorized, highlightTerms = [] }: Props) {
  const { item, loading, error } = useDetailFetch(id, itemType)
  const { show } = useToast()
  const [editMode, setEditMode] = useState(false)

  // DomainOverlaySection の useEffect deps を安定させるため useCallback で参照固定
  // (fallback の `() => {}` を JSX 内で毎回 new すると無限再 fetch ループになる)
  const stableOnUnauthorized = useCallback(() => {
    if (onUnauthorized) onUnauthorized()
  }, [onUnauthorized])

  // フォーム state
  const [formInit, setFormInit] = useState(false)
  const [visibility, setVisibility] = useState('private')
  const [lifecycle, setLifecycle] = useState('active')
  const [staleBy, setStaleBy] = useState('')
  const [reviewBy, setReviewBy] = useState('')
  const [sourceProject, setSourceProject] = useState('')
  const [previousVersion, setPreviousVersion] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)

  // id 変更時に edit mode リセット
  useEffect(() => { setEditMode(false); setFormInit(false) }, [id])

  // item が読み込まれたらフォームを初期化
  useEffect(() => {
    if (!item || formInit) return
    setVisibility(item.visibility ?? 'private')
    setLifecycle(item.lifecycle_stage ?? 'active')
    setStaleBy(item.stale_by ?? '')
    setReviewBy(item.review_by ?? '')
    setSourceProject(item.source?.project ?? '')
    setPreviousVersion(item.previous_version ?? '')
    setFormInit(true)
  }, [item, formInit])

  // Recently opened に push (Phase 3-C)
  useEffect(() => {
    if (item) pushRecentlyOpened({ id: item.id, itemType: item.itemType, title: item.title })
  }, [item])

  if (loading) return <div className="p-6 text-sm text-text-subtle">Loading…</div>
  if (error) return <EmptyState title="Failed to load" description={error} />
  if (!item) return null

  const isArticle = item.itemType === 'article'

  async function save() {
    if (!isArticle) return
    const willPublic = visibility !== 'private' && item!.visibility === 'private'
    const willDeprecate = lifecycle === 'deprecated' && item!.lifecycle_stage !== 'deprecated'
    if (willPublic || willDeprecate) {
      const msg = willPublic ? 'visibility を public に変更します。続行?' : 'lifecycle を deprecated に変更します。続行?'
      if (!window.confirm(msg)) return
    }
    setSaving(true)
    try {
      const body: Record<string, unknown> = {
        visibility,
        article_meta: { lifecycle_stage: lifecycle, stale_by: staleBy || null, review_by: reviewBy || null },
        source_project: sourceProject || null,
      }
      if (previousVersion !== (item!.previous_version ?? '')) {
        body.previous_version = previousVersion || null
      }
      await apiFetch(`/api/articles/${id}`, { method: 'PATCH', body })
      show('Saved', { kind: 'success' })
      setEditMode(false)
    } catch (e: unknown) {
      show(`Save failed: ${e instanceof Error ? e.message : 'unknown'}`, { kind: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // header actions: permalink (Entry/Article 両方) + edit toggle (Article のみ)
  const headerActions = (
    <>
      <PermalinkButton id={id} visibility={item.visibility ?? null} showId={true} />
      {isArticle && (
        <button
          type="button"
          onClick={() => setEditMode(m => !m)}
          title={editMode ? 'Switch to read mode' : 'Edit article'}
          className="text-xs px-2 py-1 rounded text-text-subtle hover:bg-bg-overlay hover:text-text-base transition"
        >
          {editMode ? '📖 Read' : '✏ Edit'}
        </button>
      )}
    </>
  )

  return (
    <div className="h-full flex flex-col">
      <Detail
        title={item.title}
        body={editMode ? undefined : item.content}  // edit mode では本文非表示
        one_line={editMode ? undefined : item.one_line}
        key_points={editMode ? [] : item.key_points}
        origin={item.origin}
        subjectivity={item.subjectivity}
        visibility={item.visibility}
        lifecycle_stage={item.lifecycle_stage ?? undefined}
        source={item.source}
        source_actor={item.source_actor}
        source_context={item.source_context}
        declared_domains={item.declared_domains}
        curated={item.curated}
        inferred={item.inferred}
        created_at={item.created_at}
        highlightTerms={highlightTerms}
        article_meta={editMode ? undefined : item.article_meta}
        source_entry_ids={editMode ? [] : item.source_entry_ids}
        source_article_ids={editMode ? [] : item.source_article_ids}
        previous_version={editMode ? null : item.previous_version}
        decision={editMode ? null : item.decision}
        migration_status={item.migration_status}
        text_schema_status={item.text_schema_status}
        has_decision={item.has_decision}
        x_status={item.x_status}
        decision_size_class={item.decision_size_class}
        onClose={onClose}
        headerActions={headerActions}
        contextSection={!editMode && isArticle ? (
          <JudgmentFeedbackPanel articleId={id} />
        ) : editMode && isArticle ? (
          <>
            <ArticlePatchForm
              visibility={visibility} setVisibility={setVisibility}
              lifecycle={lifecycle} setLifecycle={setLifecycle}
              staleBy={staleBy} setStaleBy={setStaleBy}
              reviewBy={reviewBy} setReviewBy={setReviewBy}
              sourceProject={sourceProject} setSourceProject={setSourceProject}
              previousVersion={previousVersion} setPreviousVersion={setPreviousVersion}
              showAdvanced={showAdvanced} setShowAdvanced={setShowAdvanced}
              saving={saving} onSave={save}
            />
            <div className="border-t border-border-subtle pt-4 mt-4">
              <h3 className="text-xs uppercase tracking-wide text-text-subtle mb-3">Domain curated overlay</h3>
              <DomainOverlaySection
                itemType={itemType}
                itemId={id}
                onUnauthorized={stableOnUnauthorized}
              />
            </div>
          </>
        ) : editMode && !isArticle ? (
          <p className="text-sm text-text-muted">Entry は P1 (immutable) により評価面 PATCH 不可。誤りがあれば訂正 Entry を新規作成してください。</p>
        ) : undefined}
      />
    </div>
  )
}

interface PatchFormProps {
  visibility: string; setVisibility: (v: string) => void
  lifecycle: string; setLifecycle: (v: string) => void
  staleBy: string; setStaleBy: (v: string) => void
  reviewBy: string; setReviewBy: (v: string) => void
  sourceProject: string; setSourceProject: (v: string) => void
  previousVersion: string; setPreviousVersion: (v: string) => void
  showAdvanced: boolean; setShowAdvanced: (v: boolean) => void
  saving: boolean; onSave: () => void
}

function ArticlePatchForm({
  visibility, setVisibility,
  lifecycle, setLifecycle,
  staleBy, setStaleBy,
  reviewBy, setReviewBy,
  sourceProject, setSourceProject,
  previousVersion, setPreviousVersion,
  showAdvanced, setShowAdvanced,
  saving, onSave,
}: PatchFormProps) {
  return (
    <div className="space-y-4 text-sm">
      <h3 className="text-xs uppercase tracking-wide text-text-subtle">Evaluation fields (PATCH)</h3>
      <div className="grid grid-cols-[120px_1fr] gap-y-3 gap-x-3 items-center">
        <label className="text-text-subtle">Visibility</label>
        <select value={visibility} onChange={e => setVisibility(e.target.value)}
          className="px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm">
          <option value="private">private</option>
          <option value="public-searchable">public-searchable</option>
          <option value="public-unsearchable">public-unsearchable</option>
        </select>
        <label className="text-text-subtle">Lifecycle</label>
        <select value={lifecycle} onChange={e => setLifecycle(e.target.value)}
          className="px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm">
          <option value="active">active</option>
          <option value="draft">draft</option>
          <option value="deprecated">deprecated</option>
          <option value="experimental">experimental</option>
        </select>
        <label className="text-text-subtle">Stale by</label>
        <input type="datetime-local" value={dtForInput(staleBy)} onChange={e => setStaleBy(e.target.value ? new Date(e.target.value).toISOString() : '')}
          className="px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm" />
        <label className="text-text-subtle">Review by</label>
        <input type="datetime-local" value={dtForInput(reviewBy)} onChange={e => setReviewBy(e.target.value ? new Date(e.target.value).toISOString() : '')}
          className="px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm" />
        <label className="text-text-subtle">Source project</label>
        <input type="text" value={sourceProject} onChange={e => setSourceProject(e.target.value)}
          className="px-2 py-1.5 bg-bg-base border border-border-subtle rounded text-sm w-full" />
      </div>

      {/* Advanced */}
      <div className="border-t border-border-subtle pt-3">
        <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs text-text-subtle hover:text-text-base flex items-center gap-1">
          <span>{showAdvanced ? '▾' : '▸'}</span> Advanced
        </button>
        {showAdvanced && (
          <div className="mt-3 grid grid-cols-[120px_1fr] gap-y-3 gap-x-3 items-center">
            <label className="text-text-subtle text-xs">Previous version<br/><span className="text-state-warning">(危険)</span></label>
            <input type="text" value={previousVersion} onChange={e => setPreviousVersion(e.target.value)}
              placeholder="UUID or empty"
              className="px-2 py-1.5 bg-bg-base border border-state-warning/50 rounded text-sm font-mono w-full" />
          </div>
        )}
      </div>

      <div className="pt-2">
        <button type="button" onClick={onSave} disabled={saving}
          className="px-4 py-2 bg-accent-primary text-bg-surface rounded text-sm hover:opacity-90 disabled:opacity-60">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  )
}

function dtForInput(iso: string): string {
  if (!iso) return ''
  try { return new Date(iso).toISOString().slice(0, 16) } catch { return '' }
}
